/*
 * The GPIO binding contract is deliberately independent from the Home
 * Assistant automation AST.  This module is the import/export boundary for
 * local GPIO bindings; editors and metadata providers can consume its
 * structured diagnostics later without changing the wire contract.
 */

export const GPIO_MAX_BINDINGS = 8
export const GPIO_ENTITY_ID_MAX_LENGTH = 23
export const GPIO_EVENTS = Object.freeze(['changed', 'click', 'down', 'up'])
export const GPIO_SOURCE_PROPERTIES = Object.freeze(['val', 'state'])
export const GPIO_PROPERTIES = Object.freeze(['state', 'brightness'])
// State bindings may also read a sensor's `value` (ADC, Designer-first:
// docs/GPIO-LOGIC-FIRMWARE-SPEC.md §5).
export const GPIO_STATE_PROPERTIES = Object.freeze([...GPIO_PROPERTIES, 'value'])
export const GPIO_TARGET_PROPERTIES = Object.freeze(['val', 'state', 'text'])
// Objects whose `val` can show a sensor value.
export const GPIO_VALUE_TARGET_TYPES = Object.freeze(['slider', 'arc', 'bar', 'gauge', 'linemeter'])

const EVENT_SET = new Set(GPIO_EVENTS)
const SOURCE_SET = new Set(GPIO_SOURCE_PROPERTIES)
const PROPERTY_SET = new Set(GPIO_PROPERTIES)
const STATE_PROPERTY_SET = new Set(GPIO_STATE_PROPERTIES)
const TARGET_SET = new Set(GPIO_TARGET_PROPERTIES)
const INT32_MIN = -2147483648
const INT32_MAX = 2147483647

const EVENT_KEYS = new Set(['events', ...GPIO_EVENTS])
const KNOWN_BINDING_KEYS = new Set([
  'event', 'source_property', 'entity', 'property', 'target_property',
  'in', 'out', 'in_min', 'in_max', 'out_min', 'out_max', 'mapping',
])

const clone = value => {
  if (Array.isArray(value)) return value.map(clone)
  if (isPlainObject(value)) return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, clone(item)]))
  return value
}

function isPlainObject(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

function issue(severity, code, path, message, binding) {
  return { severity, code, path, message, ...(binding === undefined ? {} : { binding }) }
}

function pushIssue(target, severity, code, path, message, binding) {
  target.push(issue(severity, code, path, message, binding))
}

function isInteger(value) {
  return typeof value === 'number' && Number.isInteger(value) && value >= INT32_MIN && value <= INT32_MAX
}

function isEntityId(value) {
  return typeof value === 'string'
    && value.length > 0
    && value.length <= GPIO_ENTITY_ID_MAX_LENGTH
    && /^[A-Za-z_][A-Za-z0-9_-]*$/.test(value)
}

function hasOwn(value, key) {
  return isPlainObject(value) && Object.prototype.hasOwnProperty.call(value, key)
}

function rangeValue(raw, mapping, name) {
  const source = hasOwn(raw, name) ? raw[name] : mapping?.[name]
  if (Array.isArray(source)) {
    if (source.length !== 2 || !isInteger(source[0]) || !isInteger(source[1])) {
      return { present: true, valid: false }
    }
    return { present: true, valid: true, values: source }
  }
  if (source !== undefined) {
    return { present: true, valid: isInteger(source), value: source }
  }
  return { present: false, valid: true }
}

function normalizeRangeFields(raw, result, diagnostics, path) {
  const mapping = isPlainObject(raw.mapping) ? raw.mapping : undefined
  let mappingValid = raw.mapping === undefined || mapping !== undefined

  for (const direction of ['in', 'out']) {
    const min = rangeValue(raw, mapping, `${direction}_min`)
    const max = rangeValue(raw, mapping, `${direction}_max`)
    const pairSource = hasOwn(raw, direction) ? raw[direction] : mapping?.[direction]
    const pair = pairSource === undefined
      ? { present: false, valid: true }
      : Array.isArray(pairSource) && pairSource.length === 2 && isInteger(pairSource[0]) && isInteger(pairSource[1])
        ? { present: true, valid: true, values: pairSource }
        : { present: true, valid: false }

    if (!min.valid || !max.valid || !pair.valid) {
      mappingValid = false
      pushIssue(diagnostics, 'error', 'invalid-mapping', `${path}.${direction}`,
        `${direction} mapping must contain integer values (or a two-item integer array).`)
    }

    const minValue = min.values?.[0] ?? min.value ?? pair.values?.[0]
    const maxValue = max.values?.[1] ?? max.value ?? pair.values?.[1]
    if (minValue !== undefined) result[`${direction}_min`] = minValue
    if (maxValue !== undefined) result[`${direction}_max`] = maxValue
  }

  if (raw.mapping !== undefined && !mappingValid) {
    // Keep malformed legacy data in design saves. Runtime export is blocked by
    // the structured error; the importer must never silently destroy it.
    result.mapping = clone(raw.mapping)
  } else if (isPlainObject(raw.mapping)) {
    delete result.mapping
    const unknownMapping = Object.fromEntries(Object.entries(raw.mapping)
      .filter(([key]) => !['in', 'out', 'in_min', 'in_max', 'out_min', 'out_max'].includes(key))
      .map(([key, value]) => [key, clone(value)]))
    if (Object.keys(unknownMapping).length > 0) result.mapping = unknownMapping
  } else if (raw.mapping !== undefined) {
    result.mapping = clone(raw.mapping)
  }

  // `in`/`out` are known migration aliases and are omitted after successful
  // normalization. A malformed alias remains available through `mapping` only
  // when it came from mapping; direct malformed aliases stay on the binding.
  if (mappingValid) {
    delete result.in
    delete result.out
  }
}

function normalizeBinding(raw, kind, eventName, index, diagnostics) {
  if (!isPlainObject(raw)) {
    pushIssue(diagnostics, 'error', 'binding-not-object', `${kind}[${index}]`,
      `${kind === 'events' ? 'Event' : 'State'} binding must be an object.`, index)
    return clone(raw)
  }

  const result = clone(raw)
  if (eventName !== undefined && result.event === undefined) result.event = eventName
  // Firmware defaults source_property to state for a state target and val for
  // brightness. Materialize that legacy default so the internal model is
  // canonical while preserving the firmware's existing meaning.
  if (kind === 'events' && result.source_property === undefined && PROPERTY_SET.has(result.property)) {
    result.source_property = result.property === 'state' ? 'state' : 'val'
  }
  normalizeRangeFields(raw, result, diagnostics, `${kind}[${index}]`)
  return result
}

function appendEventEntries(entries, value, eventName) {
  if (Array.isArray(value)) {
    value.forEach(item => entries.push({ value: item, eventName }))
  } else {
    entries.push({ value, eventName })
  }
}

function collectEvents(raw, diagnostics) {
  const entries = []
  if (Array.isArray(raw.events)) raw.events.forEach(value => entries.push({ value }))
  else if (isPlainObject(raw.events)) {
    for (const [eventName, value] of Object.entries(raw.events)) {
      if (EVENT_SET.has(eventName)) appendEventEntries(entries, value, eventName)
      else pushIssue(diagnostics, 'warning', 'unknown-event-map-key', `events.${eventName}`,
        `Unknown GPIO event-map key '${eventName}' was preserved.`)
    }
  } else if (raw.events !== undefined) {
    pushIssue(diagnostics, 'error', 'events-not-array', 'events', 'GPIO events must be an array or legacy event map.')
  }

  for (const eventName of GPIO_EVENTS) {
    if (raw[eventName] !== undefined) appendEventEntries(entries, raw[eventName], eventName)
  }

  return entries.map(({ value, eventName }, index) => normalizeBinding(value, 'events', eventName, index, diagnostics))
}

function collectState(raw, diagnostics) {
  if (Array.isArray(raw.state)) {
    return raw.state.map((value, index) => normalizeBinding(value, 'state', undefined, index, diagnostics))
  }
  if (isPlainObject(raw.state)) return [normalizeBinding(raw.state, 'state', undefined, 0, diagnostics)]
  if (raw.state !== undefined) {
    pushIssue(diagnostics, 'error', 'state-not-array', 'state', 'GPIO state must be an array or one state binding object.')
  }
  return []
}

/**
 * Normalize a raw `gpio_bindings` value. The returned value is the inner
 * object, not `{ gpio_bindings: ... }`, so it can be stored directly on a
 * Designer object and later wrapped by ObjectExport.
 */
export function normalizeGpioBindings(input) {
  if (input === undefined) return { value: undefined, diagnostics: [] }

  const wrapped = isPlainObject(input) && hasOwn(input, 'gpio_bindings')
  const raw = wrapped ? input.gpio_bindings : input
  const diagnostics = []
  if (!isPlainObject(raw)) {
    pushIssue(diagnostics, 'error', 'bindings-not-object', 'gpio_bindings',
      'gpio_bindings must be an object containing events and state arrays.')
    return { value: clone(raw), diagnostics }
  }

  const value = {}
  value.events = collectEvents(raw, diagnostics)
  value.state = collectState(raw, diagnostics)

  // Preserve unknown top-level fields for forward compatibility. They remain
  // opaque to validation but are not lost by a save/load round-trip.
  for (const [key, item] of Object.entries(raw)) {
    if (EVENT_KEYS.has(key) || key === 'state') continue
    value[key] = clone(item)
  }

  return { value, diagnostics }
}

export function canonicalizeGpioBindings(input) {
  const normalized = normalizeGpioBindings(input)
  if (normalized.value === undefined) return undefined
  return clone(normalized.value)
}

export function hasGpioBindings(input) {
  if (input === undefined || input === null) return false
  if (!isPlainObject(input)) return true
  return (Array.isArray(input.events) && input.events.length > 0)
    || (Array.isArray(input.state) && input.state.length > 0)
    || Object.keys(input).some(key => !['events', 'state'].includes(key))
}

function explicitRange(binding, direction) {
  return [`${direction}_min`, `${direction}_max`, direction].some(key => hasOwn(binding, key))
}

function addRangeDiagnostics(binding, path, diagnostics) {
  for (const direction of ['in', 'out']) {
    const minKey = `${direction}_min`
    const maxKey = `${direction}_max`
    const hasMin = hasOwn(binding, minKey)
    const hasMax = hasOwn(binding, maxKey)
    if (hasMin && !isInteger(binding[minKey])) {
      pushIssue(diagnostics, 'error', 'invalid-range', `${path}.${minKey}`, `${minKey} must be a signed 32-bit integer.`)
    }
    if (hasMax && !isInteger(binding[maxKey])) {
      pushIssue(diagnostics, 'error', 'invalid-range', `${path}.${maxKey}`, `${maxKey} must be a signed 32-bit integer.`)
    }
    if (hasMin && hasMax && isInteger(binding[minKey]) && isInteger(binding[maxKey])
      && direction === 'in' && binding[minKey] === binding[maxKey]) {
      pushIssue(diagnostics, 'error', 'zero-width-input-range', path,
        'The GPIO input range must not have equal in_min and in_max.')
    }
  }
}

function eventCombinationValid(objectType, event, property) {
  const type = objectType === 'button' ? 'btn' : objectType
  if (type === 'slider' || type === 'arc') return event === 'changed' && property === 'brightness'
  if (type === 'switch') return event === 'changed' && property === 'state'
  if (type === 'btn') return ['click', 'down', 'up'].includes(event) && property === 'state'
  if (type === 'btnmatrix') return event === 'changed' && ['state', 'brightness'].includes(property)
  return false
}

/** Whether the firmware can bind `objectType.event` to the entity `property` (gpio_bindings.events). */
export function isGpioEventBindingSupported(objectType, event, property) {
  return EVENT_SET.has(event) && PROPERTY_SET.has(property) && eventCombinationValid(objectType, event, property)
}

function stateCombinationValid(objectType, objectConfig, property, target, binding) {
  const type = objectType === 'button' ? 'btn' : objectType
  if (property === 'value') {
    if (target === 'text') return type === 'label' && !explicitRange(binding, 'out')
    return target === 'val' && GPIO_VALUE_TARGET_TYPES.includes(type)
  }
  if (target === 'text') return type === 'label' && property === 'state' && !explicitRange(binding, 'out')
  if (type === 'slider' || type === 'arc') {
    return target === 'val' && (property === 'brightness' || (property === 'state' && explicitRange(binding, 'out')))
  }
  if (type === 'switch') return ['val', 'state'].includes(target) && property === 'state'
  if (type === 'btn') {
    return ['val', 'state'].includes(target) && property === 'state' && objectConfig?.toggle === true
  }
  return false
}

function validateEvent(binding, index, objectType, diagnostics) {
  const path = `events[${index}]`
  if (!isPlainObject(binding)) return
  if (!EVENT_SET.has(binding.event)) {
    pushIssue(diagnostics, 'error', 'invalid-event', `${path}.event`,
      `event must be one of: ${GPIO_EVENTS.join(', ')}.`, index)
  }
  if (!SOURCE_SET.has(binding.source_property)) {
    pushIssue(diagnostics, 'error', 'invalid-source-property', `${path}.source_property`,
      `source_property must be one of: ${GPIO_SOURCE_PROPERTIES.join(', ')}.`, index)
  }
  if (binding.property === undefined || binding.property === null || binding.property === '') {
    pushIssue(diagnostics, 'error', 'missing-property', `${path}.property`,
      'GPIO event binding requires property.', index)
  } else if (!PROPERTY_SET.has(binding.property)) {
    pushIssue(diagnostics, 'warning', 'unknown-gpio-property', `${path}.property`,
      `Unknown GPIO property '${binding.property}' was preserved for forward compatibility.`, index)
  }
  if (!isEntityId(binding.entity)) {
    pushIssue(diagnostics, 'error', 'invalid-entity-id', `${path}.entity`,
      'entity must match the firmware GPIO ID format and be at most 23 characters.', index)
  }
  if (EVENT_SET.has(binding.event) && SOURCE_SET.has(binding.source_property) && PROPERTY_SET.has(binding.property)
    && !eventCombinationValid(objectType, binding.event, binding.property)) {
    pushIssue(diagnostics, 'error', 'unsupported-event-combination', path,
      `The firmware does not support ${objectType}.${binding.event} → ${binding.property}.`, index)
  }
  addRangeDiagnostics(binding, path, diagnostics)
}

function validateState(binding, index, objectType, objectConfig, diagnostics) {
  const path = `state[${index}]`
  if (!isPlainObject(binding)) return
  if (binding.property === undefined || binding.property === null || binding.property === '') {
    pushIssue(diagnostics, 'error', 'missing-property', `${path}.property`,
      'GPIO state binding requires property.', index)
  } else if (!STATE_PROPERTY_SET.has(binding.property)) {
    pushIssue(diagnostics, 'warning', 'unknown-gpio-property', `${path}.property`,
      `Unknown GPIO property '${binding.property}' was preserved for forward compatibility.`, index)
  } else if (binding.property === 'value') {
    pushIssue(diagnostics, 'warning', 'value-binding-firmware', `${path}.property`,
      'Sensor value bindings need newer firmware; older firmware ignores this binding.', index)
  }
  if (!TARGET_SET.has(binding.target_property)) {
    pushIssue(diagnostics, 'error', 'invalid-target-property', `${path}.target_property`,
      `target_property must be one of: ${GPIO_TARGET_PROPERTIES.join(', ')}.`, index)
  }
  if (!isEntityId(binding.entity)) {
    pushIssue(diagnostics, 'error', 'invalid-entity-id', `${path}.entity`,
      'entity must match the firmware GPIO ID format and be at most 23 characters.', index)
  }
  if (STATE_PROPERTY_SET.has(binding.property) && TARGET_SET.has(binding.target_property)
    && !stateCombinationValid(objectType, objectConfig, binding.property, binding.target_property, binding)) {
    pushIssue(diagnostics, 'error', 'unsupported-state-combination', path,
      `The firmware does not support ${objectType}.${binding.property} → ${binding.target_property}.`, index)
  }
  addRangeDiagnostics(binding, path, diagnostics)
  if (binding.target_property === 'text' && explicitRange(binding, 'out')) {
    pushIssue(diagnostics, 'error', 'text-mapping-not-supported', `${path}.out_min`,
      'Firmware label text bindings show the entity value as text and do not accept an output mapping.', index)
  }
}

function metadataDiagnostics(binding, path, metadata, diagnostics, kind) {
  if (!metadata) {
    pushIssue(diagnostics, 'warning', 'entity-unverified', `${path}.entity`,
      'Entity could not be verified because plate metadata is unavailable.')
    return
  }
  const entities = Array.isArray(metadata) ? metadata : metadata.entities
  if (!Array.isArray(entities)) return
  const entity = entities.find(item => item?.id === binding.entity)
  if (!entity) {
    pushIssue(diagnostics, 'warning', 'entity-not-found', `${path}.entity`,
      `Entity '${binding.entity}' is not present in the selected plate descriptor.`)
    return
  }
  const properties = entity.properties ?? {}
  const advertised = binding.property && (Object.hasOwn(properties, binding.property)
    || (Array.isArray(entity.capabilities) ? entity.capabilities : []).includes(binding.property))
  const metadataComplete = Object.hasOwn(entity, 'properties') || Object.hasOwn(entity, 'capabilities')
  if (binding.property && !advertised) {
    pushIssue(diagnostics, metadataComplete ? 'error' : 'warning', 'property-not-advertised', `${path}.property`,
      `Property '${binding.property}' is not advertised by entity '${binding.entity}'.`)
    return
  }
  // GPIO event bindings write to the physical entity. A descriptor-known
  // read-only property is therefore a real contract error. State bindings
  // only read the entity and remain valid for read-only properties.
  if (kind === 'events' && binding.property && properties[binding.property]?.writable === false) {
    pushIssue(diagnostics, 'error', 'entity-property-read-only', `${path}.property`,
      `Property '${binding.property}' on entity '${binding.entity}' is read-only for an Event Action.`)
  }
}

/** Validate a raw or normalized GPIO binding object. */
export function validateGpioBindings(input, {
  objectType,
  objectConfig,
  metadata,
} = {}) {
  const normalized = normalizeGpioBindings(input)
  const diagnostics = [...normalized.diagnostics]
  const value = normalized.value
  if (value === undefined) return { valid: true, errors: [], warnings: [], infos: [], diagnostics, value }
  if (!isPlainObject(value)) {
    pushIssue(diagnostics, 'error', 'bindings-not-object', 'gpio_bindings',
      'gpio_bindings must be an object containing events and state arrays.')
  } else {
    if (value.events.length > GPIO_MAX_BINDINGS) {
      pushIssue(diagnostics, 'error', 'too-many-events', 'events',
        `Firmware supports at most ${GPIO_MAX_BINDINGS} GPIO event bindings.`)
    }
    if (value.state.length > GPIO_MAX_BINDINGS) {
      pushIssue(diagnostics, 'error', 'too-many-state-bindings', 'state',
        `Firmware supports at most ${GPIO_MAX_BINDINGS} GPIO state bindings.`)
    }
    value.events.forEach((binding, index) => {
      validateEvent(binding, index, objectType, diagnostics)
      if (isPlainObject(binding) && isEntityId(binding.entity)) metadataDiagnostics(binding, `events[${index}]`, metadata, diagnostics, 'events')
    })
    value.state.forEach((binding, index) => {
      validateState(binding, index, objectType, objectConfig, diagnostics)
      if (isPlainObject(binding) && isEntityId(binding.entity)) metadataDiagnostics(binding, `state[${index}]`, metadata, diagnostics, 'state')
    })
    if (objectConfig?.groupid && value.events.length > 0) {
      pushIssue(diagnostics, 'warning', 'group-id-conflict', 'events',
        'Firmware may skip direct GPIO actions on grouped objects to preserve Group ID semantics.')
    }
  }
  return {
    valid: !diagnostics.some(item => item.severity === 'error'),
    errors: diagnostics.filter(item => item.severity === 'error'),
    warnings: diagnostics.filter(item => item.severity === 'warning'),
    infos: diagnostics.filter(item => item.severity === 'info'),
    diagnostics,
    value,
  }
}

export class GpioBindingModel {
  constructor(input, options = {}) {
    const normalized = normalizeGpioBindings(input)
    this.bindings = normalized.value
    this.normalizationDiagnostics = normalized.diagnostics
    this.options = options
  }

  validate(options = {}) {
    const result = validateGpioBindings(this.bindings, { ...this.options, ...options })
    result.errors = result.diagnostics.filter(item => item.severity === 'error')
    result.warnings = result.diagnostics.filter(item => item.severity === 'warning')
    result.infos = result.diagnostics.filter(item => item.severity === 'info')
    result.valid = result.errors.length === 0
    return result
  }

  toJSON() {
    return canonicalizeGpioBindings(this.bindings)
  }

  static fromJSON(input, options = {}) {
    return new GpioBindingModel(input, options)
  }
}

export default GpioBindingModel

